/**
 * The pay WINDOW — how often Michael pays the crew, and what the Pay page and the crew's
 * phone add the weekly ledger up into.
 *
 * The ledger itself is weekly (crew_pay_entries.pay_period_start is a Monday, due the
 * following Monday — unchanged). The window is only how the pages group those weeks:
 *
 *   biweekly — two Mon–Sun pay weeks, a Week B week followed by a Week A week, ending on a
 *              Sunday, due the Monday after. "Everyone is paid through Sep 6" was the end of
 *              the window Aug 24 – Sep 6, so windows start on Mondays 14 days apart from
 *              Sep 7 2026 (WINDOW_ANCHOR). The rule since Sep 2026 and the default.
 *   monthly  — the 1st of a month through its last day, due the 1st of the next month
 *              ("1st – 1st", owner, Oct 27 2026). A ledger week that straddles the 1st belongs
 *              to the month its MONDAY (pay_period_start) falls in: Mon Oct 26 – Sun Nov 1 is
 *              October's. Every week is in exactly one month, so nothing is paid twice or missed.
 *   week     — one ledger week (the Pay page's 1-week view; not a setting value).
 *
 * Settings → Crew & pay → "Crew pay window" (app_settings.pay_window) picks biweekly or
 * monthly; GET /crew-pay/window reads it. server/services/crewPay.js has the same rules
 * (windowFor / shiftWindow), pinned by server/tests/payWindow.test.js — change both.
 */
export const WINDOW_ANCHOR = '2026-09-07';   // a Monday that starts a Week B pay week

/** What the setting can be. */
export type PayWindowMode = 'biweekly' | 'monthly';
/** What a page can show: a setting value, or one ledger week. */
export type PayViewMode = PayWindowMode | 'week';
export const PAY_WINDOW_DEFAULT: PayWindowMode = 'biweekly';

const DAY = 86400000;
const noon = (iso: string) => new Date(iso.slice(0, 10) + 'T12:00:00Z');
const iso = (d: Date) => d.toISOString().slice(0, 10);
const plus = (isoDate: string, days: number) => iso(new Date(noon(isoDate).getTime() + days * DAY));

export const plusDays = plus;

export type PayWindow = { mode: PayViewMode; start: string; end: string; due: string; weeks: string[] };

/** A setting value → 'biweekly' | 'monthly' (anything else is the default). */
export function payWindowModeOf(value: unknown): PayWindowMode {
  const v = String(value ?? '').trim().toLowerCase();
  return v === 'monthly' ? 'monthly' : 'biweekly';
}

/** Monday of the Mon–Sun pay week containing a date (same rule as the server). */
export function weekStartOf(dateISO: string): string {
  const d = noon(dateISO);
  const sinceMonday = (d.getUTCDay() + 6) % 7;
  return plus(dateISO, -sinceMonday);
}

/**
 * The window containing a date. `weeks` are the Mondays (pay_period_start) of the ledger
 * weeks inside it, in order.
 */
export function windowFor(dateISO: string, mode: PayViewMode = PAY_WINDOW_DEFAULT): PayWindow {
  const date = dateISO.slice(0, 10);
  if (mode === 'week') {
    const start = weekStartOf(date);
    return { mode, start, end: plus(start, 6), due: plus(start, 7), weeks: [start] };
  }
  if (mode === 'monthly') {
    const [y, m] = date.split('-').map(Number);
    const start = `${y}-${String(m).padStart(2, '0')}-01`;
    const due = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`;   // next month's 1st
    const end = plus(due, -1);
    const weeks: string[] = [];
    let monday = weekStartOf(start);
    if (monday < start) monday = plus(monday, 7);   // the week straddling the 1st is last month's
    for (; monday <= end; monday = plus(monday, 7)) weeks.push(monday);
    return { mode, start, end, due, weeks };
  }
  const wk = weekStartOf(date);
  const diff = Math.round((noon(wk).getTime() - noon(WINDOW_ANCHOR).getTime()) / DAY);
  const offset = ((diff % 14) + 14) % 14;          // 0 or 7
  const start = plus(wk, -offset);
  return { mode: 'biweekly', start, end: plus(start, 13), due: plus(start, 14), weeks: [start, plus(start, 7)] };
}

/** The window N windows after (or before) this one, of the same kind. */
export function shiftWindow(w: PayWindow, n: number): PayWindow {
  let out = w;
  const step = Math.sign(n);
  for (let i = 0; i < Math.abs(n); i++) out = windowFor(step > 0 ? plus(out.end, 1) : plus(out.start, -1), w.mode);
  return out;
}

/** "Pay window" / "Month" / "Week of" — the noun a page uses for the window on screen. */
export function windowNoun(mode: PayViewMode): string {
  return mode === 'monthly' ? 'month' : mode === 'week' ? 'week' : 'pay window';
}

/** Today's date in Central time, as YYYY-MM-DD. */
export function todayCT(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
}

/**
 * ANY DATES — a range Michael types instead of a window (Nov 1 2026: "find pay through any
 * dates"). Matched the way the windows are, by the LEDGER WEEK: an entry is in the range when
 * its Monday (pay_period_start) falls in start..end, inclusive — so a range that is exactly a
 * month shows what the Month view shows, and back-to-back ranges never double up a week.
 * Up to two years. Same rules as services/crewPay.rangeFor (server/tests/crew-pay-range.test.js).
 */
export const RANGE_MAX_DAYS = 731;   // two years, a leap day included

export type PayRange = { start: string; end: string; days: number; weeks: string[]; words: string };

/** A real YYYY-MM-DD, else null. */
export function isoDateOf(value: unknown): string | null {
  const s = String(value ?? '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = noon(s);
  return Number.isNaN(d.getTime()) || iso(d) !== s ? null : s;
}

/** The Mondays of the ledger weeks whose Monday falls in start..end, in order. */
export function rangeMondays(start: string, end: string): string[] {
  const weeks: string[] = [];
  let monday = weekStartOf(start);
  if (monday < start) monday = plus(monday, 7);
  for (; monday <= end; monday = plus(monday, 7)) weeks.push(monday);
  return weeks;
}

/** "Oct 1 – Nov 1, 2026 · 4 pay weeks" — the year once when both ends share it. */
export function rangeWords(start: string, end: string, weeks: string[]): string {
  const md = (s: string) => noon(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const y = (s: string) => s.slice(0, 4);
  const dates = y(start) === y(end) ? `${md(start)} – ${md(end)}, ${y(end)}` : `${md(start)}, ${y(start)} – ${md(end)}, ${y(end)}`;
  const n = weeks.length;
  return `${dates} · ${n === 0 ? 'no pay week starts in it' : `${n} pay week${n === 1 ? '' : 's'}`}`;
}

/** Why start..end is not a range the ledger can show, or null when it is. */
export function rangeProblem(startValue: unknown, endValue: unknown): string | null {
  const start = isoDateOf(startValue), end = isoDateOf(endValue);
  if (!start || !end) return 'Pick both dates';
  if (end < start) return 'The end date is before the start date';
  const days = Math.round((noon(end).getTime() - noon(start).getTime()) / DAY) + 1;
  if (days > RANGE_MAX_DAYS) return 'A range can cover up to two years';
  return null;
}

/** The range start..end as the ledger sees it, or null when rangeProblem says why not. */
export function rangeFor(startValue: unknown, endValue: unknown): PayRange | null {
  if (rangeProblem(startValue, endValue)) return null;
  const start = isoDateOf(startValue) as string, end = isoDateOf(endValue) as string;
  const days = Math.round((noon(end).getTime() - noon(start).getTime()) / DAY) + 1;
  const weeks = rangeMondays(start, end);
  return { start, end, days, weeks, words: rangeWords(start, end, weeks) };
}

/** The range N ranges after (or before) this one: the same number of days, back to back. */
export function shiftRange(r: PayRange, n: number): PayRange | null {
  return rangeFor(plus(r.start, n * r.days), plus(r.end, n * r.days));
}

/** The quick picks on the Pay page's "Any dates" control, from today (Central). */
export function quickRanges(today: string = todayCT()): { label: string; start: string; end: string }[] {
  const [y, m] = today.split('-').map(Number);
  const first = (yy: number, mm: number) => `${yy}-${String(mm).padStart(2, '0')}-01`;
  const lastOf = (yy: number, mm: number) => plus(mm === 12 ? first(yy + 1, 1) : first(yy, mm + 1), -1);
  const pm = m === 1 ? 12 : m - 1, py = m === 1 ? y - 1 : y;
  return [
    { label: 'This month', start: first(y, m), end: lastOf(y, m) },
    { label: 'Last month', start: first(py, pm), end: lastOf(py, pm) },
    { label: 'This year', start: `${y}-01-01`, end: `${y}-12-31` },
    { label: 'Last 90 days', start: plus(today, -89), end: today },
  ];
}
